import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Flex, Text } from 'rebass/styled-components';
import { ChevronDown, SidebarOpen } from 'lucide-react';
import { Tooltip } from '@getro/rombo';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import { preventUnhandled } from '@atlaskit/pragmatic-drag-and-drop/prevent-unhandled';
import invariant from 'tiny-invariant';
import { toggleMenu, toggleSection } from '../../../features/mainMenu';
import { activeNetworkSelector } from '../../../redux/selectors';
import { useMainMenu } from '../../../hooks/useMainMenu';
import { MenuNav } from './menuNav';
import ListLinks from './listLinks';
import { NavHeader } from './navHeader';
import { UserAndHelpMenu } from '../userAndHelpMenu';

export const MainLayoutMenu = ({ width, setMenuWidth, contentRef }) => {
  const activeNetwork = useSelector(activeNetworkSelector);
  const isMenuOpen = useSelector((state) => state.mainMenu.isOpen);
  const sectionsExpanded = useSelector((state) => state.mainMenu.sectionsExpanded);
  const [initialWidth, setInitialWidth] = useState();
  const { items } = useMainMenu(activeNetwork);
  const dispatch = useDispatch();

  const sidebarRef = useRef(null);
  const dividerRef = useRef(null);
  const [state, setState] = useState({
    type: 'idle',
  });

  const widths = {
    start: 240,
    min: 200,
    max: 440,
  };

  const getProposedWidth = useCallback(
    ({ initialWidth: startWidth, location }) => {
      const diffX = location.current.input.clientX - location.initial.input.clientX;
      const proposedWidth = startWidth + diffX;

      // ensure we don't go below the min or above the max allowed widths
      return Math.min(Math.max(widths.min, proposedWidth), widths.max);
    },
    [widths.min, widths.max],
  );

  useEffect(() => {
    const divider = dividerRef.current;
    invariant(divider);

    const cleanup = draggable({
      element: divider,
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        // we will be moving the line to indicate a drag
        // we can disable the native drag preview
        disableNativeDragPreview({ nativeSetDragImage });
        // we don't want any native drop animation for when the user
        // does not drop on a drop target. we want the drag to finish immediately
        preventUnhandled.start();
      },
      onDragStart() {
        setInitialWidth(width);
        setState({ type: 'dragging' });
        contentRef.current?.style.setProperty('transition', 'none');
      },
      onDrag({ location }) {
        const newWidth = getProposedWidth({ initialWidth, location });
        contentRef.current?.style.setProperty('margin-left', `${newWidth}px`);
        sidebarRef.current?.style.setProperty('width', `${newWidth}px`);
      },
      onDrop({ location }) {
        preventUnhandled.stop();
        setState({ type: 'idle' });
        const newWidth = getProposedWidth({ initialWidth, location });
        setMenuWidth(newWidth);
        localStorage.setItem('sidebar-width', newWidth.toString());
        contentRef.current?.style.removeProperty('margin-left');
        contentRef.current?.style.removeProperty('transition');
        sidebarRef.current?.style.removeProperty('width');
      },
    });

    return cleanup;
  }, [width, setMenuWidth, getProposedWidth, contentRef, initialWidth]);

  return (
    <>
      {/* Overlay */}
      <Box
        display={[isMenuOpen ? 'block' : 'none', isMenuOpen ? 'block' : 'none', 'none']}
        bg="neutral.20"
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          zIndex: 98,
          opacity: 0.8,
        }}
        onClick={() => dispatch(toggleMenu())}
      />

      {/* Sidebar */}
      <Flex
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: [99, 99, 15],
          boxShadow: [isMenuOpen ? 'medium' : 'none', 'none', 'none'],
          transition: 'transform 0.15s ease-in-out',
          transform: isMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
        }}
      >
        <Flex
          ref={sidebarRef}
          className="main-menu"
          height="100vh"
          width={['calc(100vw - 80px)', 'calc(100vw - 80px)', width]}
          maxWidth="440px"
          flexDirection="column"
          bg={['neutral.0', 'neutral.0', 'neutral.20']}
          sx={{
            borderRight: 1,
            borderRightColor: 'neutral.100',
          }}
        >
          {/* Expand sidebar button - visible when collapsed, fixed at 4px from left */}
          {!isMenuOpen && (
            <Tooltip
              position="right"
              content="Expand sidebar"
              trigger={
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    position: 'absolute',
                    right: '-28px',
                    top: '12px',
                    zIndex: [99, 99, 16],
                    width: '24px',
                    height: '24px',
                    cursor: 'pointer',
                  }}
                  onClick={() => dispatch(toggleMenu())}
                  aria-label="Expand sidebar"
                >
                  <Box as={SidebarOpen} size="16px" aria-hidden="true" />
                </Flex>
              }
            />
          )}
          <Box
            ref={dividerRef}
            sx={{
              display: ['none', 'none', 'block'],
              position: 'absolute',
              right: 0,
              top: 0,
              width: '4px',
              height: '100%',
              borderRight: '2px solid',
              borderRightColor: state.type === 'dragging' ? 'purple.400' : 'transparent',
              cursor: 'col-resize',
              transition: 'border-right-color 0.15s ease-in-out',
              '&:hover': {
                borderRightColor: 'purple.400',
              },
            }}
          />
          <NavHeader activeNetwork={activeNetwork} />

          <Flex flexDirection="column" flexGrow="1" mt="0px" sx={{ minHeight: 0 }}>
            {!!items.length && (
              <Box
                as="nav"
                mx="4px"
                sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: '1 1 auto', overflow: 'auto' }}
              >
                <Box
                  as="ul"
                  sx={{
                    listStyle: 'none',
                    p: 0,
                    m: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1 1 auto',
                    minHeight: 0,
                  }}
                >
                  {items.map((section) => (
                    <Box as="li" key={section.label} sx={{ display: 'flex', flexDirection: 'column' }}>
                      {section.separator && (
                        <Box mx="8px" my="8px" sx={{ borderBottom: 1, borderBottomColor: 'border.subtle' }} />
                      )}
                      {section.sectionKey && (
                        <Flex
                          sx={{
                            gap: '8px',
                            alignItems: 'center',
                            cursor: 'pointer',
                            p: '8px',
                            pr: '4px',
                            borderRadius: 'default',
                            '&:hover': {
                              bg: 'neutral.50',
                            },
                          }}
                          onClick={() => dispatch(toggleSection(section.sectionKey))}
                        >
                          {section.icon && (
                            <Box
                              as={section.icon}
                              width="16px"
                              height="16px"
                              aria-hidden="true"
                              flex="0 0 auto"
                              sx={{ color: 'neutral.400' }}
                            />
                          )}
                          <Text
                            as="div"
                            sx={{
                              fontSize: '14px',
                              fontWeight: 'medium',
                              color: 'text.main',
                              lineHeight: '16px',
                            }}
                          >
                            {section.label}
                          </Text>
                          <Box
                            as={ChevronDown}
                            size="16px"
                            aria-hidden="true"
                            flex="0 0 auto"
                            sx={{
                              ml: 'auto',
                              color: 'neutral.400',
                              transform: sectionsExpanded[section.sectionKey] ? 'rotate(0deg)' : 'rotate(-90deg)',
                              transition: 'transform 0.1s ease-in-out',
                            }}
                          />
                        </Flex>
                      )}
                      <Box
                        as="ul"
                        sx={{
                          listStyle: 'none',
                          p: 0,
                          pl: section.sectionKey ? '8px' : '0',
                          pb: section.sectionKey && sectionsExpanded[section.sectionKey] === true ? '8px' : 0,
                          m: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '1px',
                          opacity: section.sectionKey && sectionsExpanded[section.sectionKey] === false ? 0 : 1,
                          height: section.sectionKey && sectionsExpanded[section.sectionKey] === false ? 0 : 'auto',
                          overflow: 'hidden',
                          transition: 'all 0.1s ease-in-out',
                        }}
                      >
                        {(section.items ?? []).map((item) => (
                          <MenuNav
                            key={item.path || item.href || item.label}
                            path={item.path || ''}
                            href={item.href || item.path || ''}
                            label={item.label}
                            icon={item.icon}
                            type={item.type || 'internal'}
                            slug={activeNetwork.slug}
                            isBeta={item.isBeta}
                            betaVariant={item.betaVariant}
                            showUpgradeIcon={item.showUpgradeIcon}
                            tooltip={item.tooltip}
                            badge={item.badge}
                          />
                        ))}
                      </Box>
                    </Box>
                  ))}

                  {/* Lists section */}
                  {activeNetwork.connect?.hasAccess && (
                    <Box
                      as="li"
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box mx="8px" my="8px" sx={{ borderBottom: 1, borderBottomColor: 'border.subtle' }} />
                      <ListLinks slug={activeNetwork.slug} />
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Flex>
          <Box
            flexShrink={0}
            p="4px"
            sx={{ position: 'relative', backgroundColor: 'neutral.20', borderTop: 1, borderTopColor: 'border.subtle' }}
          >
            <UserAndHelpMenu />
          </Box>
        </Flex>
      </Flex>
    </>
  );
};

MainLayoutMenu.propTypes = {
  width: PropTypes.number.isRequired,
  setMenuWidth: PropTypes.func.isRequired,
  contentRef: PropTypes.object,
};

MainLayoutMenu.defaultProps = {
  contentRef: null,
};
